import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, OnInit, ViewChild, ViewEncapsulation, inject } from '@angular/core';
import { OverlayContainer } from '@angular/cdk/overlay';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { asapScheduler, filter, observeOn } from 'rxjs';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialogConfig } from '@angular/material/dialog';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { NgIcon } from '@ng-icons/core';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { LibSharedCommonModule } from '@solvace/common';
import { LibSharedComponentModule } from '@solvace/component';
import { SvcDialogPrimaryComponent, SvcDialogService } from '@solvace/component/svc-dialogs';
import { SvcReactionModule } from '@solvace/feature/general/svc-reaction';
import { SvcAudioToTextButtonModule } from '@solvace/feature/general/svc-audio-to-text-button';
import { SvcFormCustomFieldModule } from '@solvace/feature/general/svc-form-custom-field';
import { DialogAlertTabs, DialogAlertType, SvcModalAlertComponent, SvcModalAlertModule, SvcModalAlertOverride } from '@solvace/feature/modals/svc-modal-alert';
import { provideSolvaceIcons } from '@solvace/zard/svz-icons/solvace-icons';
import { AlertRecordDialogService } from './alert-record-dialog.service';
import { AlertRecordActionPlansComponent } from './alert-record-action-plans/alert-record-action-plans.component';
import { ZardDropdownImports } from '@solvace/zard/shared/components/dropdown/dropdown.imports';
import { ZardTooltipImports } from '@solvace/zard/shared/components/tooltip/tooltip.imports';
import { SvzMediaCarouselComponent } from '@solvace/zard/svz-media-carousel/svz-media-carousel.component';
import { SvzUserAvatarComponent } from '@solvace/zard/svz-user-avatar/svz-user-avatar.component';
import { SvzBookmarkButtonComponent } from '@solvace/zard/svz-bookmark-button/svz-bookmark-button.component';
import { SvzLikeButtonComponent } from '@solvace/zard/svz-like-button/svz-like-button.component';
import { SvzTotalsStatusComponent } from '@solvace/zard/svz-totals-status/svz-totals-status.component';
import { ISvzUserAvatar, UserAvatarSize } from '@solvace/zard/svz-user-avatar/interfaces/svz-user-avatar.interface';
import { SvcLocationsService } from '@solvace/feature/general/svc-location-field/service/svc-location.service';
import { ReactionTypeEnum } from '@solvace/feature/general/svc-reaction/models/reaction-type.model';
import { SvzChipComponent } from '@solvace/zard/svz-controls/svz-chip/svz-chip/svz-chip.component';
import { SvzChipGroupedComponent } from '@solvace/zard/svz-controls/svz-chip/svz-chip-grouped/svz-chip-grouped.component';
import { ISvzChipGroupedOption } from '@solvace/zard/svz-controls/svz-chip/interfaces/svz-chip.interface';
import { SvzSelectComponent } from '@solvace/zard/svz-controls/svz-select/svz-select.component';
import { SvzDatePickerComponent } from '@solvace/zard/svz-controls/svz-date-picker/svz-date-picker.component';
import { SvzTextAreaComponent } from '@solvace/zard/svz-controls/svz-text-area/svz-text-area.component';
import { SvzTabNavComponent, SvzTabNavItem } from '@solvace/zard/svz-tab-nav/svz-tab-nav.component';
import { SvzStatusBadgeComponent } from '@solvace/zard/svz-status-badge/svz-status-badge.component';
import { ALERT_RECORD_STATE_PILLS, AlertRecordData, AlertRecordStatePill } from './alert-record-state';

const RECORD_TAB_KEYS: Record<DialogAlertTabs, string> = {
  [DialogAlertTabs.Alert]: 'alert',
  [DialogAlertTabs.ActionPlan]: 'action-plan',
  [DialogAlertTabs.Feed]: 'feed',
};

const RECORD_TAB_BY_KEY: Record<string, DialogAlertTabs> = Object.fromEntries(
  Object.entries(RECORD_TAB_KEYS).map(([tab, key]) => [key, Number(tab) as DialogAlertTabs]),
);

export interface AlertRecordView {
  originator: ISvzUserAvatar | null;
  team: string;
  place: string;
  classification: string;
  failure: string;
  materialType: string;
  material: string;
  supplier: string;
  hashtags: string[];
  observations: string;
  openingDate: string | null;
  occurrenceDate: string | null;
}

interface OptionLike {
  value?: unknown;
  label?: string | number;
  text?: string | number;
}

const SINGLE_CHIP_CONTROLS = ['locationId', 'classificationId', 'failureId', 'materialTypeId', 'productId', 'supplierId'] as const;

const firstValue = (value: unknown): unknown => (Array.isArray(value) ? value[0] : value);

const optionLabel = (options: OptionLike[] | undefined, value: unknown): string => {
  const option = options?.find((item) => item.value === firstValue(value));
  const label = option?.label ?? option?.text;
  return label === undefined || label === null ? '' : `${label}`;
};

// The legacy modal pads its pane 20px top and bottom at md+, which pushes this dialog 20px below the prototype's centre.
const LEGACY_PANEL_PADDING_CLASS = 'md:py-5';

// Scope of `styles/_alert-zone.scss`: the Alert app sets it itself, other hosts get it from the modal while it is open.
const ALERT_ZONE_CLASS = 'alert-zone';

export const ALERT_RECORD_DIALOG_CONFIG: Omit<MatDialogConfig, 'data'> = {
  width: '800px',
  maxWidth: '96vw',
  maxHeight: '92vh',
  panelClass: 'alert-record-dialog',
  backdropClass: 'alert-record-backdrop',
  autoFocus: false,
};

@Component({
  selector: 'app-alert-record-modal',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Default,
  templateUrl: './alert-record-modal.component.html',
  styleUrls: ['./alert-record-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    ReactiveFormsModule,
    TranslocoModule,
    NgIcon,
    NgxSkeletonLoaderModule,
    LibSharedCommonModule,
    LibSharedComponentModule,
    SvcReactionModule,
    SvcAudioToTextButtonModule,
    SvcFormCustomFieldModule,
    SvcModalAlertModule,
    SvzTabNavComponent,
    SvzMediaCarouselComponent,
    SvzUserAvatarComponent,
    SvzBookmarkButtonComponent,
    SvzLikeButtonComponent,
    SvzTotalsStatusComponent,
    SvzChipComponent,
    SvzChipGroupedComponent,
    SvzSelectComponent,
    SvzDatePickerComponent,
    SvzTextAreaComponent,
    SvzStatusBadgeComponent,
    ...ZardDropdownImports,
    ...ZardTooltipImports,
    AlertRecordActionPlansComponent,
  ],
  providers: [provideSolvaceIcons(), { provide: SvcDialogService, useClass: AlertRecordDialogService }],
  host: { class: 'flex max-h-[92vh] flex-col bg-white text-[#2a2a2a] max-[640px]:h-[100dvh] max-[640px]:max-h-[100dvh]' },
})
export class AlertRecordModalComponent extends SvcModalAlertComponent implements OnInit {
  @ViewChild('recordHeader') public recordHeader: ElementRef<HTMLElement>;
  @ViewChild('recordForm') public recordForm: ElementRef<HTMLFormElement>;

  private readonly transloco = inject(TranslocoService);
  private readonly locationsService = inject(SvcLocationsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly overlayContainer = inject(OverlayContainer);
  private locationNames = new Map<number, string>();
  private recordViewCache: { inputs: unknown[]; view: AlertRecordView };
  public locationsLoading = true;
  public placeOptions: ISvzChipGroupedOption[] = [];
  protected readonly likeReaction = ReactionTypeEnum.LIKE;

  public get descriptionControl(): FormControl {
    return this.formAlert?.controls['description'] as FormControl;
  }
  private recordTabsCache: { signature: string; items: SvzTabNavItem[] };

  public get recordTabs(): SvzTabNavItem[] {
    const signature = `${this.showTabActionPlan}|${this.isShowTabActionPlanLoading}|${this.isAddMode}`;
    if (this.recordTabsCache?.signature !== signature) {
      this.recordTabsCache = { signature, items: this.buildRecordTabs() };
    }
    return this.recordTabsCache.items;
  }

  public override get isInitialLoading(): boolean {
    return this.isLoading || this.isShowTabActionPlanLoading || this.isShowVoiceToTextButtonLoading || this.isAllCombosLoading || this.locationsLoading;
  }

  public get recordView(): AlertRecordView {
    const value = this.formAlert?.getRawValue() ?? {};
    const combos = this.alertCombos ?? {};
    const inputs = [
      JSON.stringify(value),
      this.userAvatar,
      this.locationNames,
      combos.teams,
      combos.classification,
      combos.failures,
      combos.materialType,
      combos.materials,
      combos.suppliers,
      combos.hashtags,
    ];
    const cached = this.recordViewCache;
    if (!cached || inputs.some((input, index) => input !== cached.inputs[index])) {
      this.recordViewCache = { inputs, view: this.buildRecordView(value) };
    }
    return this.recordViewCache.view;
  }

  public get activeTabKey(): string {
    return RECORD_TAB_KEYS[this.tabIndex as DialogAlertTabs];
  }

  public selectRecordTab(item: SvzTabNavItem): void {
    this.tabIndex = RECORD_TAB_BY_KEY[item.key];
  }

  public get titleKey(): string {
    return this.alertMode?.mode === DialogAlertType.add ? 'Novo alerta' : 'Alerta';
  }

  public get statePill(): AlertRecordStatePill | null {
    const state = (this.alert as AlertRecordData | undefined)?.actionPlanState;
    return state ? ALERT_RECORD_STATE_PILLS[state] ?? null : null;
  }

  public override ngOnInit(): void {
    this.dialogRef.removePanelClass(LEGACY_PANEL_PADDING_CLASS);
    this.joinAlertZone();
    super.ngOnInit();
    this.keepSingleChipValuesAsArrays();
    this.locationsService.initialOptions$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(({ areas, locations }) => {
      this.locationNames = new Map([...(areas ?? []), ...(locations ?? [])].map((uda) => [uda.udaId, uda.udaName]));
      this.placeOptions = [
        { group: this.transloco.translate('Áreas'), options: (areas ?? []).map((uda) => ({ label: uda.udaName, value: uda.udaId })) },
        { group: this.transloco.translate('Localidades'), options: (locations ?? []).map((uda) => ({ label: uda.udaName, value: uda.udaId })) },
      ];
    });
    this.locationsService.initialOptionsIsLoading$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((loading) => (this.locationsLoading = loading));
    this.locationsService.getInitialOptions();
  }

  private keepSingleChipValuesAsArrays(): void {
    for (const name of SINGLE_CHIP_CONTROLS) {
      const control = this.formAlert?.get(name);
      control?.valueChanges
        .pipe(
          filter((value) => value !== null && value !== undefined && !Array.isArray(value)),
          observeOn(asapScheduler),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe((value) => {
          if (control.value === value) control.setValue([value]);
        });
    }
  }

  private joinAlertZone(): void {
    const container = this.overlayContainer.getContainerElement();
    if (container.classList.contains(ALERT_ZONE_CLASS)) return;
    container.classList.add(ALERT_ZONE_CLASS);
    this.destroyRef.onDestroy(() => container.classList.remove(ALERT_ZONE_CLASS));
  }

  private buildRecordView(value: Record<string, unknown>): AlertRecordView {
    const combos = this.alertCombos ?? {};
    const hashtagIds = (value['hashtagIds'] as unknown[]) ?? [];
    const groupedOptions: unknown[] = (combos.hashtags ?? []).flatMap((group) => group.options ?? []);
    const hashtagOptions = groupedOptions.filter((option): option is OptionLike => typeof option === 'object' && option !== null && 'value' in option);
    const hasOriginator = !!(this.userAvatar?.name || this.userAvatar?.picture);
    return {
      originator: hasOriginator
        ? {
            name: this.userAvatar.name,
            picture: this.userAvatar.picture,
            initialsColor: '#e6e6ea',
            initialsTextColor: '#444',
            size: 20 as UserAvatarSize,
            initialsFontSize: 7,
            showBorder: false,
          }
        : null,
      team: optionLabel(combos.teams, value['teamId']),
      place: this.locationNames.get(firstValue(value['locationId']) as number) ?? '',
      classification: optionLabel(combos.classification, value['classificationId']),
      failure: optionLabel(combos.failures, value['failureId']),
      materialType: optionLabel(combos.materialType, value['materialTypeId']),
      material: optionLabel(combos.materials, value['productId']),
      supplier: optionLabel(combos.suppliers, value['supplierId']),
      hashtags: hashtagIds.map((id) => optionLabel(hashtagOptions, id)).filter(Boolean),
      observations: (value['description'] as string) ?? '',
      openingDate: (value['createDate'] as string) ?? null,
      occurrenceDate: (value['occurrenceDate'] as string) ?? null,
    };
  }

  private buildRecordTabs(): SvzTabNavItem[] {
    const tabs: SvzTabNavItem[] = [{ key: RECORD_TAB_KEYS[DialogAlertTabs.Alert], label: this.transloco.translate('Alerta') }];
    if (this.showTabActionPlan || this.isShowTabActionPlanLoading) {
      tabs.push({ key: RECORD_TAB_KEYS[DialogAlertTabs.ActionPlan], label: this.transloco.translate('Plano de Ação'), disabled: this.isShowTabActionPlanLoading });
    }
    tabs.push({ key: RECORD_TAB_KEYS[DialogAlertTabs.Feed], label: this.transloco.translate('Feed'), disabled: this.isAddMode });
    return tabs;
  }

  public override print(): void {
    const legacyDialog = this.dialog;
    this.dialog = { dialogTitleContainer: this.recordHeader } as SvcDialogPrimaryComponent;
    super.print();
    this.dialog = legacyDialog;
  }

  public override createOrUpdateAlert(): void {
    super.createOrUpdateAlert();
    if (this.formAlert?.invalid) this.focusFirstInvalidField();
  }

  private focusFirstInvalidField(): void {
    const field = this.recordForm?.nativeElement.querySelector<HTMLElement>('.ng-invalid[formcontrolname]');
    if (!field) return;
    field.scrollIntoView({ block: 'center' });
    field.querySelector<HTMLElement>('button, input, [tabindex]:not([tabindex="-1"])')?.focus();
  }
}

export const ALERT_RECORD_MODAL_OVERRIDE: SvcModalAlertOverride = {
  component: AlertRecordModalComponent,
  config: ALERT_RECORD_DIALOG_CONFIG,
};
